/**
 * 关键指标趋势图组件
 * 包含：CardCommon 包装 + 双轴趋势图（声量 + 声量占比） + 关键词更新标记散点 + 内置下载功能 + 内置 tooltip 底部按钮
 *
 * 规范：
 * - 使用 EChartCommon 组件
 * - 使用 chartAllColors 颜色常量
 * - 使用 formatChartParamsDate 函数格式化 tooltip
 * - 数据格式遵循 TRequestBackType 规范
 * - 内置下载功能，使用 DBO_U.download
 * - 内置 useChartFootBtn 处理 tooltip 底部按钮
 */
import React, { useMemo, useCallback } from 'react';
import CardCommon from '@op/components/CardCommon';
import EChartCommon from '@op/components/EChartCommon';
import useLanguage from '@op/hooks/useLanguage';
import { useChartFootBtn } from '@op/hooks/useChartFootBtn';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_V, DBO_EC, DBO_U } from '@op/utils/format';
import { chartAllColors } from '@op/constants';
import { ClassSpecial } from '@op/constants/common';
import type { TRequestBackType } from '@op/utils/requestUtil';

interface KeyMetricsTrendChartProps {
  /** 图表数据（TRequestBackType 格式） */
  data: TRequestBackType | null;
  /** 图表高度 */
  height?: number;
  /** 加载状态 */
  loading?: boolean;
  /** 空数据状态 */
  empty?: boolean;
  /** 过滤条件 */
  filter?: any;
  /** CardCommon className */
  className?: string;
  toFeeds?: () => void;
}

const KeyMetricsTrendChart: React.FC<KeyMetricsTrendChartProps> = ({
  data,
  height = 350,
  loading = false,
  empty = false,
  filter,
  className,
  toFeeds,
}) => {
  const { formatMessage } = useLanguage();
  // 图表 tooltip 底部按钮
  const { getBottomClickBtn, divElementRef } = useChartFootBtn({
    filter,
    buttons: [
      {
        key: 'feeds',
        labelId: 'Click to go to [Feeds]',
      },
    ],
    onClick: ({ btnKey }) => {
      if (btnKey === 'feeds') {
        toFeeds?.();
      }
    },
  });
  // 内置下载处理 - 使用 data.tableRecord
  const handleDownload = useCallback(() => { 
    DBO_U.download({
      key: 'WeeklyKeyMetrics',
      fileName: 'Weekly Report - Key Metrics',
      filter,
      data: data?.tableRecord || [],
      auxiliaryParams: { filter },
    });
  }, [filter, data]);

  // 系列配置（标签 + 类型 + Y轴 + 颜色）
  interface SeriesConfig {
    label: string;
    type: 'line' | 'scatter';
    yAxisIndex?: number;
    color: string;
  }

  const seriesConfig = useMemo<Record<string, SeriesConfig>>(
    () => ({
      mentions: {
        label: formatMessage({ id: 'Security-Related Mentions' }),
        type: 'line',
        yAxisIndex: 0,
        color: chartAllColors[0],
      },
      mentions_ratio: {
        label: formatMessage({
          id: 'Share of Security Mentions (Security Mentions / Total Game Mentions)',
        }),
        type: 'line',
        yAxisIndex: 1,
        color: chartAllColors[1],
      },
      new_add_keyword: {
        label: formatMessage({ id: 'Add Security-Related Crawling Keywords' }),
        type: 'scatter',
        color: '#faad14',
      },
    }),
    [formatMessage],
  );

  // Memoize tooltip formatter，避免每次创建新函数
  const tooltipFormatter = useCallback(
    (params: any) => {
      const axisValue = params?.[0]?.axisValue || '';
      // 过滤掉 Keyword Updated 散点系列
      const filteredParams = params.filter(
        (item: any) => item.seriesName !== seriesConfig.new_add_keyword?.label,
      );
      return formatChartParamsDate({
        title: DBO_EC.toolTime(axisValue, filter?.date_type || 'daily'),
        rightTitle: seriesConfig.mentions?.label,
        params: filteredParams,
        listTitleFunc: (item: any) => item.seriesName,
        valueFormat: (value: number, item: any) => {
          if (item.seriesName === seriesConfig.mentions_ratio?.label) {
            return DBO_V.ratio(value * 100);
          }
          return DBO_V.reviews(value);
        },
        footerText: getBottomClickBtn({
          StartTime: axisValue,
          EndTime: axisValue,
        }),
      });
    },
    [seriesConfig, getBottomClickBtn, filter],
  );

  const chartOptions = useMemo(() => {
    // 空数据时返回最小有效配置
    if (!data || data.isEmpty) {
      return {
        xAxis: { type: 'category', data: [] },
        yAxis: [{ type: 'value' }, { type: 'value', position: 'right' }],
        series: [],
      };
    }

    // 循环 data.series 创建 ECharts series 配置，同时计算右侧Y轴（mentions_ratio）的 isMin
    let isMin = false;
    const series = (data.series || []).map((s) => {
      // 计算右侧Y轴（mentions_ratio）的 isMin：最大值 < 0.1 时显示两位小数
      if (s.name === 'mentions_ratio' && s.data?.length) {
        isMin = Math.max(...(s.data || [])) < 0.1;
      }
      const config = seriesConfig[s.name];
      const isScatter = config?.type === 'scatter';

      const seriesItem: any = {
        name: config?.label || s.name,
        type: config?.type || 'line',
        yAxisIndex: config?.yAxisIndex,
        itemStyle: { color: config?.color },
      };

      // 数据处理
      if (isScatter) {
        // 将 new_add_keyword 数据转换为散点格式 [日期索引, 值]
        seriesItem.data = s.data
          .map((val: number, idx: number) => (val ? [idx, val] : null))
          .filter(Boolean);
        seriesItem.symbol = 'circle';
        seriesItem.symbolSize = 12;
        seriesItem.z = 10;
      } else {
        seriesItem.data = s.data;
        seriesItem.lineStyle = { width: 2 };
      }

      return seriesItem;
    });

    return {
      tooltip: {
        trigger: 'axis',
        enterable: true,
        className: ClassSpecial.CHART_TOOLTIP_SCROLL,
        position: DBO_EC.enterablePosition,
        formatter: tooltipFormatter,
      },
      legend: {
        data: Object.values(seriesConfig).map((config) => config.label),
        bottom: 0,
      },
      grid: {
        top: 20,
      },
      xAxis: {
        type: 'category',
        data: data.xAxis || [],
        axisLabel: {
          formatter: DBO_EC.timeX,
        },
      },
      yAxis: [
        {
          type: 'value',
          position: 'left',
          axisLabel: {
            formatter: DBO_EC.reviews,
          },
        },
        {
          type: 'value',
          position: 'right',
          axisLabel: {
            formatter: (value: number) => {
              return DBO_EC.percent(value, isMin);
            },
          },
        },
      ],
      series,
    };
  }, [data, seriesConfig, tooltipFormatter]);

  return (
    <CardCommon
      className={className}
      title={formatMessage({ id: 'Mentions (Security-Related) Trend' })}
      loading={loading}
      empty={empty}
      loadingHeight={height}
      emptyHeight={height}
      hasDownload={!empty}
      handleDownloadOnClick={handleDownload}
      logBtnId="WR_KeyMetrics"
      contentCard
    >
      <div ref={divElementRef}>
        <EChartCommon
          leftTitle={seriesConfig.mentions?.label}
          rightTitle={seriesConfig.mentions_ratio?.label}
          options={chartOptions}
          height={height}
          optionsProps={{ type: 'line' }}
        />
      </div>
    </CardCommon>
  );
};

export default KeyMetricsTrendChart;
